import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getServices, getSiteCopy } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, serviceLd, faqLd, breadcrumbLd } from "@/lib/seo";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { ArrowRight } from "lucide-react";
import { amp } from "@/lib/amp";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const AUDIENCES = ["founders", "enterprises", "entrepreneurs", "nonprofits"];

// Audience-aware sub-header. The Home doors route here with ?audience=.
const AUDIENCE_INTRO = {
  founders:
    "You are building something. Most founders start with the Sprint and climb only when the roadmap calls for it.",
  enterprises:
    "Start where the exposure is greatest. You lead at a large organization, and the AI ambition is running ahead of the governance and infrastructure to support it. These tiers carry the documentation, decision rights, and compliance that hold up under board and regulatory scrutiny. Independent by design. We have no platform to sell you.",
  entrepreneurs:
    "You have the idea. Make it real on paper before you spend a dollar building. Most people in your position pay to build the wrong thing first. We define the customer, pressure-test the model, and hand you a roadmap, so the build, when it comes, is the right one.",
  nonprofits:
    "You run a mission-driven organization. The fractional and modernization tiers carry the governance, continuity, and funder-ready reporting your board and your funders expect, without adding a full-time executive to the budget. We build to the workflows you already run, never the reverse.",
};

export default function Services() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openBrief } = useBrief();
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: getServices });

  const audienceParam = searchParams.get("audience");
  const audience = AUDIENCES.includes(audienceParam) ? audienceParam : "founders";
  const intro = AUDIENCE_INTRO[audience] || copy.services_intro;

  // Audience-scoped ladder, in tier order.
  const visible = services
    .filter((s) => (s.audience || "founders") === audience)
    .sort((a, b) => (a.tier_order || 99) - (b.tier_order || 99));

  const goToSprint = (s) => {
    track("door_click", { target: "sprint", offer: s.slug, location: "services_ladder" });
    navigate(`/services/${s.link_slug || s.slug}`);
  };
  const requestBrief = (slug) => {
    track("door_click", { target: "brief_request", offer: slug, location: "services_ladder" });
    openBrief(slug);
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="services-page">
      <Seo
        {...PAGE_SEO.services}
        jsonLd={[
          serviceLd({ name: "Fractional CTO, AI Readiness and Engineering", description: PAGE_SEO.services.description, path: "/services" }),
          faqLd([
            { q: "What is a Sprint?", a: "A focused two-to-four-week engagement on one decision or build. We find what your team can do, take on the gap, and leave you with the work done and a memo on what to do next." },
          ]),
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
        ]}
      />
      <section className="exec-container pt-24 md:pt-28 pb-10">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">The ladder</p>
          <h1 className="font-extralight text-[clamp(34px,4.8vw,60px)] leading-[1.1] mt-6">
            One{" "}
            <span className="exec-serif italic font-medium text-[color:var(--copper)]">ladder.</span>
          </h1>
          <p data-testid="services-intro" className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6">
            {intro}
          </p>
        </div>
      </section>

      {/* What a Sprint is — the most common entry point */}
      <section className="exec-container pb-14" data-testid="services-sprint-explainer">
        <div className="max-w-[820px] exec-card p-8 sm:p-10">
          <p className="exec-eyebrow">How we start</p>
          <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15] mt-3">What a Sprint is.</h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">
            A focused two-to-four-week engagement on one decision or build: a UI you haven’t
            validated, a compliance certification, a feature your team can’t staff. We find what
            your team can do, take on the gap, and leave you with the work done and a memo on what
            to do next.
          </p>
        </div>
      </section>

      <section className="exec-container pb-16" data-testid="services-ladder">
        <div className="space-y-5">
          {visible.map((s) =>
            s.cta_type === "purchase" ? (
              /* Self-serve purchase tier */
              <motion.div
                {...fade}
                key={s.slug}
                data-testid="services-sprint-tier-card"
                className="exec-card p-8 sm:p-10"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="exec-eyebrow">Self-serve</span>
                      {(s.badge || s.duration_label) && (
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--grey)] font-normal">
                          {s.badge || s.duration_label}
                        </span>
                      )}
                    </div>
                    <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15] mt-3">{amp(s.name)}</h2>
                    <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">{s.description}</p>
                  </div>
                  <div className="text-left sm:text-right shrink-0">
                    <p className="font-extralight text-[clamp(28px,3.4vw,40px)] leading-none">{s.price_label}</p>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="services-sprint-purchase-button"
                    className="exec-btn exec-btn-primary"
                    onClick={() => goToSprint(s)}
                  >
                    {s.cta_label}
                  </button>
                  {s.also_brief && (
                    <button
                      data-testid="services-sprint-brief-button"
                      className="exec-btn exec-btn-secondary"
                      onClick={() => requestBrief(s.slug)}
                    >
                      Request the brief <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </motion.div>
            ) : (
              /* Consultative (brief) tier */
              <motion.div
                {...fade}
                key={s.slug}
                data-testid="services-consultative-card"
                className="exec-card p-8 sm:p-10"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                  <div className="max-w-2xl">
                    {(s.badge || s.duration_label) && (
                      <span className="exec-eyebrow block mb-2" data-testid="services-tier-badge">
                        {amp(s.badge || s.duration_label)}
                      </span>
                    )}
                    <h2 className="text-[clamp(17px,1.7vw,20px)] uppercase tracking-[0.16em] font-normal text-[color:var(--ink)]">{amp(s.name)}</h2>
                    <p className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-3">{s.description}</p>
                  </div>
                  <div className="text-left sm:text-right shrink-0">
                    <p className="exec-serif text-[20px] text-[color:var(--ink)]">{s.price_label}</p>
                  </div>
                </div>
                <div className="mt-7">
                  <button
                    data-testid="services-request-brief-button"
                    className="exec-btn exec-btn-secondary"
                    onClick={() => requestBrief(s.slug)}
                  >
                    {s.cta_label}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            )
          )}
        </div>
      </section>

      {/* Public Sector pillar */}
      <section className="exec-container pb-16" data-testid="services-public-sector-pillar">
        <div className="max-w-[820px] border-t border-[color:var(--rule)] pt-10">
          <p className="exec-eyebrow">Public sector</p>
          <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15] mt-3">A dedicated path for government and agencies.</h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">
            Municipal innovation, workforce programs, and AI readiness for agencies under real
            scrutiny. Women- and minority-owned, procurement-ready. We meet you before the RFP, not
            after.
          </p>
          <Link
            to="/public-sector"
            data-testid="services-public-sector-link"
            className="exec-link mt-7"
          >
            Technology for the public sector <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {copy.services_after && (
        <section className="exec-container pb-20">
          <div className="max-w-[820px] border-t border-[color:var(--rule)] pt-8">
            <p data-testid="services-after" className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)]">
              {copy.services_after}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
